import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';

/**
 * Reusable confirmation modal.
 *
 * Props:
 *   isOpen      boolean
 *   onClose     () => void
 *   onConfirm   () => void
 *   title       string
 *   message     string | ReactNode
 *   detail      string | ReactNode   — optional secondary info block
 *   confirmLabel string              — default "Delete"
 *   danger       boolean             — red styling (default true)
 *   loading      boolean
 */
export default function ConfirmModal({
  isOpen, onClose, onConfirm,
  title = 'Confirm Delete',
  message,
  detail,
  confirmLabel = 'Delete',
  danger = true,
  loading = false,
}) {
  if (!isOpen) return null;

  const accentColor = danger ? '#ef4444' : 'var(--zeus-gold)';
  const accentBg    = danger ? 'rgba(239,68,68,0.08)' : 'rgba(200,168,75,0.08)';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9990, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Backdrop */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />

      <div style={{
        position: 'relative', width: 380, maxWidth: '94vw',
        background: 'var(--zeus-navy)', border: `1px solid ${danger ? 'rgba(239,68,68,0.3)' : 'var(--zeus-border)'}`,
        borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
        overflow: 'hidden',
      }}>
        {/* Top accent bar */}
        <div style={{ height: 3, background: accentColor }} />

        {/* Body */}
        <div style={{ padding: '20px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
              background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={18} style={{ color: accentColor }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.97rem', color: 'var(--text-primary)', marginBottom: 4 }}>
                {title}
              </div>
              {message && (
                <div style={{ fontSize: '0.86rem', color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                  {message}
                </div>
              )}
            </div>
          </div>

          {detail && (
            <div style={{
              padding: '10px 12px', borderRadius: 8, marginBottom: 6,
              background: 'var(--zeus-navy-light)', border: '1px solid var(--zeus-border)',
              fontSize: '0.84rem', color: 'var(--text-primary)',
            }}>
              {detail}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 22px', borderTop: '1px solid var(--zeus-border)',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          background: 'rgba(0,0,0,0.1)',
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.06)', border: '1px solid var(--zeus-border)',
              color: 'var(--text-secondary)', padding: '7px 16px', borderRadius: 8,
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              background: accentColor, border: 'none', color: '#fff',
              padding: '7px 18px', borderRadius: 8, cursor: loading ? 'default' : 'pointer',
              fontSize: '0.85rem', fontWeight: 700, fontFamily: 'inherit',
              opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6,
              transition: 'opacity 0.15s',
            }}
          >
            {!loading && <Trash2 size={13} />}
            {loading ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
